// Feature: M004-F0709. Tests: T-M004-0196.
//
// Two transaction ids, one person-facing rule (Core ML001-D014 section 5e.16 and 5f.2.1.1, M001-D022-F090; owner ruling 10-09).
//   * TXN-<ORG4>-<CODE11> is THE id a person sees (base 36, fixed width, e.g. TXN-0018-5JHWEU1ES8X). Core stores it as
//     billing_transactions.txn_id and sends it as `tx_id` (txDisplayId) or `txn_id`, depending on the payload.
//   * TX-<8 hex> is the LEGACY id: the first 8 hex characters of a hand_off_id, lower case. Core's txIdFromHandOffId. It is
//     shown only when no TXN is available for that charge (displayTxId). It is never a search key once a TXN exists.
// Pure: no I/O, never throws. Anything that is not a UUID has no TX id: the answer is '' (never a placeholder).
//
// The legacy ledger alias TXN-ML001-<org>-<n> (Spec core-ledger-account-gateway 1.10.0, credit notes) is also a TXN.

export const TX_PREFIX = 'TX-';
export const TXN_PREFIX = 'TXN-';
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TX_SHAPE = /^TX-[0-9a-f]{8}$/i;
const TXN_SHAPE = /^TXN-[0-9A-Z]{4}-[0-9A-Z]{11}$/i;
const TXN_LEGACY_SHAPE = /^TXN-ML001-[0-9]+-[0-9]+$/i;

export function txIdFromHandOffId(handOffId) {
  if (typeof handOffId !== 'string' || !UUID_SHAPE.test(handOffId)) return '';
  return `${TX_PREFIX}${handOffId.slice(0, 8).toLowerCase()}`;
}

// A well-formed TXN (canonical or legacy alias), upper-cased, else ''.
export function wellFormedTxnId(value) {
  if (typeof value !== 'string') return '';
  return TXN_SHAPE.test(value) || TXN_LEGACY_SHAPE.test(value) ? value.toUpperCase() : '';
}

// A well-formed TXN or a legacy `TX-xxxxxxxx`: TXN upper-cased, TX lower-cased. Else ''.
export function wellFormedTxId(value) {
  const txn = wellFormedTxnId(value);
  if (txn) return txn;
  return typeof value === 'string' && TX_SHAPE.test(value) ? `${TX_PREFIX}${value.slice(3).toLowerCase()}` : '';
}

// THE display rule for every surface. Prefers a well-formed TXN from `txnId` (the payload's txn_id) or `txId` (the payload's
// tx_id, which Core fills with the TXN when it has one). Falls back to the legacy TX- from the hand_off_id, then to a legacy
// TX- wire value. '' when none is available: the caller then shows nothing (or a sentence without an id), never a placeholder.
export function displayTxId({ txnId, txId, handOffId } = {}) {
  const txn = wellFormedTxnId(txnId) || wellFormedTxnId(txId);
  if (txn) return txn;
  return txIdFromHandOffId(handOffId) || wellFormedTxId(txId);
}

// Two hand_off ids name the same charge when they are the same UUID, ignoring case. Never true for a blank.
export function sameHandOff(a, b) {
  return typeof a === 'string' && typeof b === 'string' && a !== '' && a.toLowerCase() === b.toLowerCase();
}

// ---- M004-F0714 (design 3.2): the finder's accepted input forms. Pure; never throws. ----
// Anything else (empty, 7 hex, non-hex, a word, a UUID-with-a-typo) is refused BEFORE any request.
const FULL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TX_IN_TEXT = /TX-([0-9a-f]{8})(?![0-9a-f])/i;
const BARE_HEX = /^[0-9a-f]{8}$/i;
const WRAPPERS = /^[\s"'`([{<‘’“”]+|[\s"'`)\]}>.,;:‘’“”]+$/g;

// The TXN is the id a person reads, so it is the search form (kind 'txn'). TX-xxxxxxxx stays accepted as an ALIAS for search
// (kind 'tx'): an old note or a Core log line still finds the charge.
const TXN_IN_TEXT = /TXN-[0-9A-Z]{4}-[0-9A-Z]{11}(?![0-9A-Z])/i;

// -> { ok: true, key, kind: 'txn' | 'tx' | 'full' } | { ok: false }. `key` is the upper-case TXN, `TX-xxxxxxxx` (lower hex) or the lower-case full id.
export function parseTxInput(raw) {
  if (typeof raw !== 'string') return { ok: false };
  const text = raw.replace(WRAPPERS, '');
  if (!text) return { ok: false };
  if (FULL_ID.test(text)) return { ok: true, key: text.toLowerCase(), kind: 'full' };
  const txn = wellFormedTxnId(text);
  if (txn) return { ok: true, key: txn, kind: 'txn' };
  if (BARE_HEX.test(text)) return { ok: true, key: `${TX_PREFIX}${text.toLowerCase()}`, kind: 'tx' };
  const inText = TX_IN_TEXT.exec(text);
  if (inText) return { ok: true, key: `${TX_PREFIX}${inText[1].toLowerCase()}`, kind: 'tx' };
  const txnInText = TXN_IN_TEXT.exec(text);
  if (txnInText) return { ok: true, key: txnInText[0].toUpperCase(), kind: 'txn' };
  return { ok: false };
}
